import { h } from 'preact';
import Store from 'store/store';
import { loadMovieLocales, enableLocaleForMovie, removeLocaleForMovie } from 'utils/baseMovies';
import * as jsonPointer from 'utils/jsonPointer';
import { getFlag } from 'utils/icons';
import { alert, Modal, showModal, toast } from 'utils/modal';
import { getCurrentRegionCode } from 'utils/studioConfig';
import { joinClasses } from 'utils/utils';

import Badge from 'components/badge';
import Button from 'components/button/button';
import SimpleField from 'components/editor/settings/simpleField';
import Spinner from 'components/spinner/spinner';

import s from 'components/movies/manageLocaleModal.sss';

class ManageLocaleModal extends Modal {
	s = s;

	get movieTitle() {
		return this.getBaseMovie()?.title || 'This Movie';
	}

	constructor(props) {
		super(props);

		this.state = {
			page: 'manage',
		};

		this.onEnableLocale = this.onEnableLocale.bind(this);
		this.onRemoveLocale = this.onRemoveLocale.bind(this);
		this.switchToManagePage = this.switchToManagePage.bind(this);
		this.switchToRemovePage = this.switchToRemovePage.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();
		this.loadRegions();
	}

	getBaseMovie() {
		if (this._baseMovie) return this._baseMovie;

		const { movieId } = this.props;
		const movies = Store.get().data.movies || {};
		const baseMovie = movies[movieId] || Object.values(movies).find(movie => movie.base_movie_id === movieId)?.base_movie;

		this._baseMovie = baseMovie;
		return baseMovie;
	}

	getLocaleStatus(locale = this.state.locale) {
		if (!locale?.localised_movie) return null;
		return locale?.localised_movie?.commits?.publish ? 'live' : 'draft';
	}

	getIsLocaleLive(locale = this.state.locale) {
		return this.getLocaleStatus(locale) === 'live';
	}

	getUpdatingLocale(locale = this.state.locale) {
		return this.state.updatingLocale?.[locale.id] || false;
	}

	setUpdatingLocale(isUpdating, locale = this.state.locale) {
		this.setState({ updatingLocale: { ...(this.state.updatingLocale || {}), [locale.id]: isUpdating } });
	}

	switchToManagePage(e) {
		e?.preventDefault?.();
		e?.stopPropagation?.();

		this.setState({ page: 'manage', locale: null, url: null });
	}

	switchToRemovePage(locale) {
		this.setState({ page: 'remove', locale, url: locale.domain_name && `https://${locale.domain_name}/` });
	}

	async loadRegions() {
		this.setState({ loading: true });

		try {
			const { movieId } = this.props;
			if (!movieId) throw new Error('There was an error loading the movie region data. Please try again later.');

			const movieLocaleData = await loadMovieLocales(movieId, true);
			this.setState({ movieLocaleData, error: null });
		} catch (error) {
			console.error(error);
			this.setState({ error });
		} finally {
			this.setState({ loading: false });
		}
	}

	async onEnableLocale(locale) {
		const { movieId } = this.props;
		this.setUpdatingLocale(true, locale);

		try {
			const updatedData = await enableLocaleForMovie(movieId, locale.id);
			this.setState({ movieLocaleData: updatedData });
		} catch (error) {
			console.error(error);
			alert('There was an error enabling the locale. Please try again later.');
		} finally {
			this.setUpdatingLocale(false, locale);
		}
	}

	async onRemoveLocale() {
		const { movieId } = this.props;
		const { locale, url } = this.state;
		this.setUpdatingLocale(true, locale);

		try {
			const movieLocaleData = await removeLocaleForMovie(movieId, locale.id, url);

			const isLive = this.getIsLocaleLive(locale);
			toast(`${locale.name || 'Locale'} ${isLive ? 'unpublished and ' : ''}removed`, { icon: 'delete', duration: 5000 });

			// if removing current locale, remove from store and navigate away
			if (locale.code === getCurrentRegionCode()) {
				const localisedMovieId = locale.localised_movie?.id;
				if (localisedMovieId) {
					const storeData = Store.get();
					const movies = jsonPointer.get(storeData.data, '/movies') || {};
					const savedMovies = jsonPointer.get(storeData.savedData, '/movies') || {};

					const updatedMovies = { ...movies };
					const updatedSavedMovies = { ...savedMovies };
					delete updatedMovies[localisedMovieId];
					delete updatedSavedMovies[localisedMovieId];

					jsonPointer.set(storeData.data, '/movies', updatedMovies);
					jsonPointer.set(storeData.savedData, '/movies', updatedSavedMovies);
				}

				this.base.close();
				window.location.hash = '#movies';
				return;
			}

			this.setState({ movieLocaleData });
			this.switchToManagePage();
		} catch (error) {
			console.error(error);
			alert('There was an error removing the locale. Please try again later.');
		} finally {
			this.setUpdatingLocale(false, locale);
		}
	}

	renderTitle() {
		if (this.state.page === 'remove') {
			const title = this.getIsLocaleLive() ? 'Remove & unpublish locale?' : 'Remove locale?';
			return <h2 class={s.title}>{title}</h2>;
		}

		return (
			<div class={s.header}>
				<h1>Manage locales</h1>
				<p>Select locales where this movie can be published.<br />Editors with permission can remove locales.</p>
			</div>
		);
	}

	renderEnabledOptions(locale) {
		const key = locale.id;

		return (props) => (
			<Button type="button" icon="dots-vertical" popovertarget={key} {...props}>
				<div popover="auto" id={key}>
					<Button type="button" icon="delete" onClick={() => this.switchToRemovePage(locale)} danger text>Remove locale</Button>
				</div>
			</Button>
		);
	}

	renderLocale(locale) {
		const { id, name, code, enabled } = locale;
		const status = this.getLocaleStatus(locale);
		const EnabledOptions = enabled ? this.renderEnabledOptions(locale) : null;

		return (
			<div class={s.locale} key={id}>
				{getFlag(code, { class: s.flag })}
				<span class={s.name}>{name}</span>
				{enabled
					? (
						<div class={s.enabled}>
							<span>Enabled</span>
							{status && <Badge type={status} />}
							<EnabledOptions class={s.options} />
						</div>
					)
					: (
						<Button
							type="button"
							icon="plus"
							class={s.enableButton}
							onClick={() => this.onEnableLocale(locale)}
							loading={this.getUpdatingLocale(locale)}
						>
							Enable locale
						</Button>
					)
				}
			</div>
		);
	}

	renderContentManage() {
		const { error, loading, movieLocaleData } = this.state;

		const locales = [
			...(movieLocaleData?.locales || []).map(locale => ({ ...locale, enabled: true })),
			...(movieLocaleData?.notEnabledLocales || []).map(locale => ({ ...locale, enabled: false }))
		].sort((a, b) => a.code.localeCompare(b.code));
		
		return (
			<div class={joinClasses(s.content, s.manage, loading && s.loading)}>
				{loading && <Spinner />}
				{!loading && error && <p class={s.error}>There was an error loading the movie region data.<br />Please try again later.</p>}
				{!loading && locales.map(locale => this.renderLocale(locale))}
			</div>
		);
	}

	renderContentRemove() {
		const { locale } = this.state;
		const warningMessage = `This will remove the ${locale.name} locale from this movie, and the movie will no longer be available for this region.`;

		if (!this.getIsLocaleLive(locale)) {
			return (
				<p>{warningMessage}</p>
			);
		}

		const movieTitle = this.movieTitle;
		const localeCode = locale.code.toUpperCase() || '';

		return (
			<div class={joinClasses(s.content, s.remove)}>
				<p>{movieTitle} has a live published site in {locale.name}</p>
				<p>{warningMessage}</p>
				<SimpleField
					label="Redirect to URL"
					value={this.state.url}
					onInput={(e) => this.setState({ url: e.target.value })}
				/>
				<label>
					<div class={s.label}>To unpublish, type in <strong>{movieTitle} {localeCode}</strong> below:</div>
					<SimpleField
						placeholder={`${movieTitle} ${localeCode}`}
						value={this.state.confirmTitle}
						onInput={(e) => this.setState({ confirmTitle: e.target.value })}
					/>
				</label>
			</div>
		);
	}

	renderContent() {
		const { page } = this.state;
		if (page === 'remove') {
			return this.renderContentRemove();
		}

		return this.renderContentManage();
	}

	renderActionButton() {
		if (this.state.page === 'remove') {
			const isUpdating = this.getUpdatingLocale();

			const CancelButton = (props) => <Button type="button" onClick={this.switchToManagePage} {...props}>Cancel</Button>;
			const RemoveButton = (props) => <Button type="button" primary danger loading={isUpdating} onClick={this.onRemoveLocale} {...props}>Remove locale</Button>;

			if (!this.getIsLocaleLive()) {
				return [
					<CancelButton />,
					<RemoveButton />
				];
			}

			// If the locale is live, require the user to type in the movie title and locale code to confirm removal
			const { confirmTitle, locale } = this.state;
			const movieTitle = this.movieTitle?.toLowerCase();
			const localeCode = locale?.code?.toLowerCase() || '';

			// Update isConfirmed to check if the user input matches the expected confirmation string
			const isConfirmed = confirmTitle && confirmTitle.toLowerCase() === `${movieTitle} ${localeCode}`;

			return [
				<CancelButton />,
				<RemoveButton disabled={!isConfirmed || isUpdating} />
			];
		}

		return (
			<Button type="submit" value="OK" primary>Done</Button>
		);
	}
}

export const showManageLocaleModal = (movieId) => {
	return showModal(ManageLocaleModal, { movieId });
};